import { ReactNode, useMemo } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Flex,
  Grid,
  LinkButton,
  Spinner,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AngleRightIcon } from '@tonic-one/react-icons';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';

//
// should use with DashboardCard, align cards in row order
// example:
//
//   <DashboardLayout>
//     <DashboardCard>...</DashboardCard>
//     <DashboardCard>...</DashboardCard>
//   </DashboardLayout>
//
//   Custom = (props) => <DashboardCard>write your components here</DashboardCard>
//
export default function DashboardLayout(props: {
  title: string;
  children: ReactNode;
  barColor: string;
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  titleBar?: ReactNode;
  titleTips?: ReactNode;
  loading?: boolean;
  loadingFailed?: boolean;
  onRetryLoading?: () => void;
}) {
  const {
    title,
    expanded,
    onExpandChange,
    titleBar,
    titleTips,
    barColor,
    children,
    loading,
    loadingFailed,
    onRetryLoading,
  } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const expandDisabled = useMemo(() => isNil(expanded), [expanded]);

  return (
    <Flex
      direction="column"
      align="flex-start"
      justify="center"
      flex={1}
      mb="4x"
      backgroundColor={
        colorMode === 'dark'
          ? colorStyle.background.secondary
          : colorStyle.background.primary
      }
    >
      <Flex width="100%">
        <Flex flex={1} alignItems="center" position="relative">
          <Flex
            flex={isNil(titleTips) ? 1 : 0}
            minWidth="max-content"
            flexShrink={0}
            height={64}
            alignItems="center"
            padding="16px"
            onClick={() => onExpandChange?.(!expanded)}
            cursor={expandDisabled ? 'default' : 'pointer'}
          >
            {expandDisabled && (
              <Flex
                width="4px"
                height="24px"
                mr="12px"
                backgroundColor={barColor || ''}
              />
            )}
            {!expandDisabled && (
              <AngleRightIcon
                width="24px"
                height="24px"
                transform={expanded ? 'rotate(90deg)' : ''}
                transition="transform 300ms cubic-bezier(0.4, 0, 0.2, 1)"
                color={colorStyle.color.secondary}
                mr="12px"
              />
            )}
            <Text
              fontWeight="400"
              fontSize="18px"
              lineHeight="24px"
              color={colorStyle.color.emphasis}
            >
              {title}
            </Text>
          </Flex>
          {(expandDisabled || expanded) && (
            <Flex
              alignItems="center"
              p="16px"
              flex={isNil(titleTips) ? 0 : 1}
              minWidth="max-content"
              flexShrink={0}
            >
              <Flex>{titleTips}</Flex>
              <Flex ml="auto">{titleBar}</Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Accordion width="100%">
        <AccordionItem isExpanded={expanded ?? true} width="100%">
          <AccordionContent
            TransitionProps={{
              appear: false,
              ...{ unmountOnExit: true },
            }}
          >
            {(loading || loadingFailed) && !expandDisabled ? (
              <Flex
                flex={1}
                height={592}
                background={colorStyle.background.tertiary}
                m="0 16px 16px"
              >
                {loadingFailed ? (
                  <Stack gap="2x" m="auto" alignItems="center">
                    <Text color={colorStyle.color.secondary}>
                      {t('common.label.dataAccessFailed')}
                    </Text>
                    <LinkButton onClick={onRetryLoading}>
                      {t('common.action.label.retry')}
                    </LinkButton>
                  </Stack>
                ) : (
                  <Spinner m="auto" />
                )}
              </Flex>
            ) : (
              <Grid
                width="calc(100% - 16px * 2)"
                height="max-content"
                m="0 16px 16px"
                gap="16px"
                templateColumns="repeat(2, calc(50% - 8px))"
              >
                {children}
              </Grid>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Flex>
  );
}
